/**
 * DORA Controls CAT Catalog
 * Based on DORA articles + RTS/ITS
 * Domains: ICT Risk Management, Incident Reporting, Resilience Testing, Third-Party Risk, Governance
 */

export type DoraControlStatus = 'IMPLEMENTED' | 'PARTIAL' | 'NOT_IMPLEMENTED' | 'N_A';
export type DoraDomain =
  | 'ICT Risk Management'
  | 'Incident Reporting'
  | 'Resilience Testing'
  | 'Third-Party Risk'
  | 'Governance';

export interface DoraControl {
  standard: string;
  controlCode: string;
  domain: DoraDomain;
  title: { en: string; es: string; ru: string };
  description: { en: string; es: string; ru: string };
  severity: 'CRITICAL' | 'MAJOR' | 'MODERATE';
  evidence: { type: string; examples: { en: string[]; es: string[]; ru: string[] } }[];
  vendorImpact: { applies: boolean };
  governanceLinks: { policies: string[]; processes: string[]; roles: string[] };
  crossMapping: { ISO27001: string[]; NIS2: string[]; ENS_ES: string[] };
  recommendations: {
    CRITICAL?: { en: string; es: string; ru: string };
    MAJOR?: { en: string; es: string; ru: string };
    MODERATE?: { en: string; es: string; ru: string };
  };
}

const ev = (type: string, en: string[], es: string[], ru: string[]) => ({
  type,
  examples: { en, es, ru },
});

const def = (
  code: string,
  domain: DoraDomain,
  title: { en: string; es: string; ru: string },
  desc: { en: string; es: string; ru: string },
  severity: 'CRITICAL' | 'MAJOR' | 'MODERATE',
  evidence: DoraControl['evidence'],
  vendor: boolean,
  rec: Partial<DoraControl['recommendations']>,
  cross: Partial<DoraControl['crossMapping']> = {}
): DoraControl => ({
  standard: 'DORA',
  controlCode: `DORA_${code}`,
  domain,
  title,
  description: desc,
  severity,
  evidence,
  vendorImpact: { applies: vendor },
  governanceLinks: { policies: [], processes: [], roles: [] },
  crossMapping: { ISO27001: [], NIS2: [], ENS_ES: [], ...cross },
  recommendations: rec,
});

// ICT Risk Management
const DORA_ICT: DoraControl[] = [
  def(
    'ICT_01',
    'ICT Risk Management',
    { en: 'ICT risk management framework', es: 'Marco de gestión de riesgos TIC', ru: 'Рамка управления ИКТ-рисками' },
    { en: 'ICT risk management framework is defined and implemented.', es: 'Marco de gestión de riesgos TIC definido e implementado.', ru: 'Рамка управления ИКТ-рисками определена и внедрена.' },
    'CRITICAL',
    [ev('policy', ['ICT RM policy', 'Risk register'], ['Política RMT', 'Registro de riesgos'], ['Политика управления рисками', 'Реестр рисков'])],
    false,
    { CRITICAL: { en: 'Implement ICT risk management framework aligned with DORA.', es: 'Implementar marco de gestión de riesgos TIC alineado con DORA.', ru: 'Внедрить рамку управления ИКТ-рисками в соответствии с DORA.' } },
    { ISO27001: ['A.5.7'], NIS2: ['Art.21'] }
  ),
  def(
    'ICT_02',
    'ICT Risk Management',
    { en: 'ICT asset identification and classification', es: 'Identificación y clasificación de activos TIC', ru: 'Идентификация и классификация активов ИКТ' },
    { en: 'Critical ICT assets are identified and classified.', es: 'Activos TIC críticos identificados y clasificados.', ru: 'Критические активы ИКТ выявлены и классифицированы.' },
    'CRITICAL',
    [ev('record', ['Asset inventory', 'Classification criteria'], ['Inventario de activos', 'Criterios de clasificación'], ['Реестр активов', 'Критерии классификации'])],
    false,
    { CRITICAL: { en: 'Create and maintain ICT asset inventory with classification.', es: 'Crear y mantener inventario de activos TIC con clasificación.', ru: 'Создать и вести реестр активов ИКТ с классификацией.' } }
  ),
  def(
    'ICT_03',
    'ICT Risk Management',
    { en: 'Vulnerability and patch management', es: 'Gestión de vulnerabilidades y parches', ru: 'Управление уязвимостями и обновлениями' },
    { en: 'Vulnerability scanning and patch management are in place.', es: 'Escaneo de vulnerabilidades y gestión de parches implementados.', ru: 'Сканирование уязвимостей и управление обновлениями реализованы.' },
    'MAJOR',
    [ev('procedure', ['Vuln scan schedule', 'Patch SLA'], ['Calendario escaneo', 'SLA parches'], ['График сканирований', 'SLA обновлений'])],
    false,
    { MAJOR: { en: 'Implement regular vulnerability scanning and patch SLAs.', es: 'Implementar escaneos regulares y SLAs de parches.', ru: 'Внедрить регулярное сканирование и SLA по обновлениям.' } }
  ),
];

// Incident Reporting
const DORA_INCIDENT: DoraControl[] = [
  def(
    'INC_01',
    'Incident Reporting',
    { en: 'Major incident classification', es: 'Clasificación de incidentes mayores', ru: 'Классификация крупных инцидентов' },
    { en: 'Major ICT incident classification and escalation criteria defined.', es: 'Criterios de clasificación y escalamiento de incidentes TIC definidos.', ru: 'Критерии классификации и эскалации крупных ИКТ-инцидентов определены.' },
    'CRITICAL',
    [ev('procedure', ['Incident classification matrix', 'Escalation procedure'], ['Matriz de clasificación', 'Procedimiento escalamiento'], ['Матрица классификации', 'Процедура эскалации'])],
    false,
    { CRITICAL: { en: 'Define major incident classification and escalation process.', es: 'Definir clasificación y proceso de escalamiento de incidentes mayores.', ru: 'Определить классификацию и процесс эскалации крупных инцидентов.' } }
  ),
  def(
    'INC_02',
    'Incident Reporting',
    { en: 'Multi-party incident coordination', es: 'Coordinación multipartes de incidentes', ru: 'Координация инцидентов с несколькими сторонами' },
    { en: 'Process for coordinating incidents with third parties exists.', es: 'Proceso de coordinación de incidentes con terceros existe.', ru: 'Процесс координации инцидентов с третьими сторонами существует.' },
    'MAJOR',
    [ev('procedure', ['Multi-party playbook', 'Coordination log'], ['Manual multipartes', 'Registro coordinación'], ['Плейбук для нескольких сторон', 'Журнал координации'])],
    true,
    { MAJOR: { en: 'Establish multi-party incident coordination procedures.', es: 'Establecer procedimientos de coordinación multipartes.', ru: 'Установить процедуры координации инцидентов с несколькими сторонами.' } }
  ),
];

// Resilience Testing
const DORA_RESILIENCE: DoraControl[] = [
  def(
    'RES_01',
    'Resilience Testing',
    { en: 'Impact tolerances for critical services', es: 'Tolerancias de impacto para servicios críticos', ru: 'Пределы воздействия для критических услуг' },
    { en: 'Impact tolerances (RTO/RPO) defined for critical ICT services.', es: 'Tolerancias de impacto (RTO/RPO) definidas para servicios TIC críticos.', ru: 'Пределы воздействия (RTO/RPO) определены для критических ИКТ-услуг.' },
    'CRITICAL',
    [ev('record', ['RTO/RPO document', 'Service inventory'], ['Documento RTO/RPO', 'Inventario servicios'], ['Документ RTO/RPO', 'Реестр услуг'])],
    false,
    { CRITICAL: { en: 'Define impact tolerances for all critical ICT services.', es: 'Definir tolerancias de impacto para todos los servicios TIC críticos.', ru: 'Определить пределы воздействия для всех критических ИКТ-услуг.' } }
  ),
  def(
    'RES_02',
    'Resilience Testing',
    { en: 'Backup and restoration testing', es: 'Pruebas de backup y restauración', ru: 'Тестирование резервирования и восстановления' },
    { en: 'Backup restoration tests performed at least quarterly.', es: 'Pruebas de restauración de backup realizadas al menos trimestralmente.', ru: 'Тесты восстановления резервных копий проводятся не реже раза в квартал.' },
    'MAJOR',
    [ev('record', ['Restore test logs', 'BCP test reports'], ['Logs de prueba', 'Informes BCP'], ['Логи тестов восстановления', 'Отчёты тестов BCP'])],
    false,
    { MAJOR: { en: 'Implement quarterly backup restoration tests.', es: 'Implementar pruebas trimestrales de restauración.', ru: 'Проводить ежеквартальные тесты восстановления.' } }
  ),
  def(
    'RES_03',
    'Resilience Testing',
    { en: 'TLPT or threat-led testing', es: 'TLPT o pruebas guiadas por amenazas', ru: 'TLPT или тестирование по сценариям угроз' },
    { en: 'TLPT or equivalent threat-led resilience testing conducted.', es: 'TLPT o pruebas de resiliencia equivalentes realizadas.', ru: 'Проводится TLPT или эквивалентное тестирование устойчивости.' },
    'MAJOR',
    [ev('record', ['TLPT report', 'Remediation plan'], ['Informe TLPT', 'Plan remediación'], ['Отчёт TLPT', 'План устранения'])],
    false,
    { MAJOR: { en: 'Conduct TLPT or threat-led penetration testing.', es: 'Realizar TLPT o pruebas de penetración guiadas por amenazas.', ru: 'Проводить TLPT или тестирование на проникновение по угрозам.' } }
  ),
];

// Third-Party Risk
const DORA_TP: DoraControl[] = [
  def(
    'TP_01',
    'Third-Party Risk',
    { en: 'Third-party ICT risk assessment', es: 'Evaluación de riesgos TIC de terceros', ru: 'Оценка ИКТ-рисков третьих сторон' },
    { en: 'Third-party ICT providers are assessed for risk before engagement.', es: 'Proveedores TIC de terceros evaluados antes del compromiso.', ru: 'Поставщики ИКТ оцениваются до начала сотрудничества.' },
    'CRITICAL',
    [ev('record', ['Vendor assessment', 'Risk score'], ['Evaluación proveedor', 'Puntuación riesgo'], ['Оценка поставщика', 'Оценка риска'])],
    true,
    { CRITICAL: { en: 'Implement third-party ICT risk assessment process.', es: 'Implementar proceso de evaluación de riesgos TIC de terceros.', ru: 'Внедрить процесс оценки ИКТ-рисков третьих сторон.' } },
    { ISO27001: ['A.5.19', 'A.5.23'], NIS2: ['Art.21'] }
  ),
  def(
    'TP_02',
    'Third-Party Risk',
    { en: 'Exit strategy for critical ICT third parties', es: 'Estrategia de salida para terceros TIC críticos', ru: 'Стратегия выхода для критических ИКТ-поставщиков' },
    { en: 'Exit strategies defined for critical ICT third-party dependencies.', es: 'Estrategias de salida definidas para dependencias críticas.', ru: 'Определены стратегии выхода для критических поставщиков ИКТ.' },
    'CRITICAL',
    [ev('procedure', ['Exit strategy document', 'Transition plan'], ['Documento estrategia salida', 'Plan transición'], ['Документ стратегии выхода', 'План перехода'])],
    true,
    { CRITICAL: { en: 'Define exit strategies for critical ICT third parties.', es: 'Definir estrategias de salida para terceros TIC críticos.', ru: 'Определить стратегии выхода для критических ИКТ-поставщиков.' } }
  ),
  def(
    'TP_03',
    'Third-Party Risk',
    { en: 'Subcontracting chain resilience', es: 'Resiliencia de cadena de subcontratación', ru: 'Устойчивость цепочки субподрядчиков' },
    { en: 'Subcontracting chains assessed for resilience.', es: 'Cadenas de subcontratación evaluadas por resiliencia.', ru: 'Цепочки субподрядчиков оцениваются на устойчивость.' },
    'MAJOR',
    [ev('record', ['Subcontractor mapping', 'Resilience assessment'], ['Mapeo subcontratistas', 'Evaluación resiliencia'], ['Карта субподрядчиков', 'Оценка устойчивости'])],
    true,
    { MAJOR: { en: 'Assess subcontracting chains for resilience.', es: 'Evaluar cadenas de subcontratación por resiliencia.', ru: 'Оценивать цепочки субподрядчиков на устойчивость.' } }
  ),
];

// Governance
const DORA_GOV: DoraControl[] = [
  def(
    'GOV_01',
    'Governance',
    { en: 'Board digital risk oversight', es: 'Supervisión del consejo sobre riesgos digitales', ru: 'Надзор совета за цифровыми рисками' },
    { en: 'Board receives and reviews digital risk reports periodically.', es: 'El consejo recibe y revisa informes de riesgos digitales periódicamente.', ru: 'Совет регулярно получает и рассматривает отчёты по цифровым рискам.' },
    'CRITICAL',
    [ev('record', ['Board pack', 'Risk report'], ['Pack consejo', 'Informe riesgos'], ['Материалы для совета', 'Отчёт по рискам'])],
    false,
    { CRITICAL: { en: 'Establish board oversight of digital/ICT risk.', es: 'Establecer supervisión del consejo sobre riesgos digitales/TIC.', ru: 'Обеспечить надзор совета за цифровыми/ИКТ-рисками.' } }
  ),
  def(
    'GOV_02',
    'Governance',
    { en: 'Post-incident board reporting', es: 'Reporte al consejo post-incidente', ru: 'Отчётность совету после инцидентов' },
    { en: 'Board is informed after major ICT incidents.', es: 'El consejo es informado tras incidentes TIC mayores.', ru: 'Совет информируется после крупных ИКТ-инцидентов.' },
    'MAJOR',
    [ev('procedure', ['Post-incident report procedure'], ['Procedimiento informe post-incidente'], ['Процедура отчёта после инцидента'])],
    false,
    { MAJOR: { en: 'Define post-incident reporting to board.', es: 'Definir reporte al consejo post-incidente.', ru: 'Определить отчётность совету после инцидентов.' } }
  ),
];

export const DORA_CONTROLS_CATALOG: DoraControl[] = [
  ...DORA_ICT,
  ...DORA_INCIDENT,
  ...DORA_RESILIENCE,
  ...DORA_TP,
  ...DORA_GOV,
];

const DOMAIN_ORDER: DoraDomain[] = [
  'ICT Risk Management',
  'Incident Reporting',
  'Resilience Testing',
  'Third-Party Risk',
  'Governance',
];

export function getDoraControlsByDomain(domain: DoraDomain): DoraControl[] {
  return DORA_CONTROLS_CATALOG.filter((c) => c.domain === domain);
}

export function getDoraControlByCode(code: string): DoraControl | undefined {
  return DORA_CONTROLS_CATALOG.find(
    (c) => c.controlCode === code || c.controlCode === `DORA_${code}`
  );
}

export function getDoraControlDomains(): DoraDomain[] {
  return DOMAIN_ORDER;
}
